/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing stream processing gates feature behavior from runtime flags. */

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isServerSideExecutionAction } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { mciKrAttributesFromSkillsAndPlugins } from '@atlassian/conversation-assistant-jsm-mci-kr/mci-kr-attributes-from-skills-and-plugins';
import {
	isStreamAUPError,
	isStreamActionComplete,
	isStreamResponse,
	type ParsedSSEMessage,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { RovoChatStreamTimeoutError } from '@atlassian/conversation-assistant-utils/RovoChatStreamTimeoutError';
import { withAsyncGeneratorTimeout } from '@atlassian/conversation-assistant-utils/withAsyncGeneratorTimeout';

import { clearStatus } from './clearStatus';
import { createReconnectableStream } from './createReconnectableStream';
import { createReconnectionMetrics } from './createReconnectionMetrics';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { extractStreamMeta } from './extractStreamMeta';
import { findStoreMessageIndex } from './findStoreMessageIndex';
import { interactionModeRequestedAttributes } from './interactionModeRequestedAttributes';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { processReconnectStreamMessage } from './processReconnectStreamMessage';
import { createReasoningChunkBuffer } from './reasoning-chunk-buffer';
import type {
	ChatStoreActionsForReconnect,
	ReconnectToInProgressStreamParams,
} from './reconnect-action';
import { withReplayFlush } from './replay-flush';
import { createStoreAbortController } from './store-abort-controller';
import { type StreamMeta } from './StreamMeta';
import type { ChatStoreAction } from './types';

const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

const ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS = 120_000;

const NO_FINAL_RESPONSE_ERROR_MESSAGE = 'The response took too long and timed out';

export const reconnectToInProgressStreamProvider: (
	params: ReconnectToInProgressStreamParams & ChatStoreActionsForReconnect,
) => ChatStoreAction =
	({
		conversationId,
		messageId,
		answerIndex: _answerIndex,
		isDeepResearch,
		lastEventId,
		setConversationData,
		abortCurrentRequest,
		onComplete,
	}) =>
	async (
		{ getState, dispatch },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			chatContextStoreActions,
			markIncompleteResearchesErrored,
			endReconnection,
			startReconnection,
			isReconnecting,
			getIsChatMounted,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamActionClassification,
			onHumanMessageIdReplaced,
			unifiedTasksActions,
		},
	) => {
		const currentConversation = getState().conversations[conversationId];

		if (abortCurrentRequest && currentConversation?.storeAbortController) {
			currentConversation.storeAbortController.abort();
		}

		// Skip reconnection if there is another operation in progress
		if (
			(currentConversation?.storeAbortController && !abortCurrentRequest) ||
			isReconnecting(conversationId)
		) {
			return;
		}

		startReconnection(conversationId, messageId);

		const reconnectionPerformanceMetrics = createReconnectionMetrics({
			isDeepResearchEnabled: isDeepResearch,
			thinkDeeperEnabled: false,
			fastModeEnabled: false,
		});

		let totalAttempts = 0;
		const reconnectStartedAt = new Date().toISOString();
		// TODO not complete information, ideally the message in progress should indicate what was the requested mode even before message is completed
		const requestedModeAttributes = interactionModeRequestedAttributes(
			isDeepResearch ? 'deep_research' : 'auto',
		);

		const metrics = {
			// Reconnection metrics
			onReconnectStarted: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.start();
				experienceTracker.start({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
						isAutoReconnect: true,
					},
				});
				trackAIMAUEvent({
					type: 'initiated',
					userGenerated: true,
					proactive: false,
					triggeredByAutoReconnect: true,
					singleInstrumentationID: messageId,
					deepResearchEnabled: isDeepResearch ?? false,
					timestamp: reconnectStartedAt,
					attributes: requestedModeAttributes,
				});
			},
			// Was able to connect to active stream
			onReconnectEstablished: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.stop();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.start();
			},
			onReconnectFailed: (error: Error) => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				experienceTracker.fail({
					teamName: 'Rovo Chat',
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
						totalAttempts,
					},
				});
				trackAIMAUEvent({
					type: 'error',
					userGenerated: true,
					proactive: false,
					triggeredByAutoReconnect: true,
					messageId,
					singleInstrumentationID: messageId,
					deepResearchEnabled: isDeepResearch ?? false,
					timestamp: new Date().toISOString(),
					attributes: requestedModeAttributes,
				});
			},
			onReconnectChunkReceived: () => {
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.stop();
			},
			onReconnectAborted: () => {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
				});
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				trackAIMAUEvent({
					type: 'dismissed',
					userGenerated: true,
					proactive: false,
					triggeredByAutoReconnect: true,
					messageId,
					singleInstrumentationID: messageId,
					deepResearchEnabled: isDeepResearch ?? false,
					timestamp: new Date().toISOString(),
					attributes: requestedModeAttributes,
				});
			},
			onReconnectSucceeded: () => {
				experienceTracker.succeed({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						totalAttempts,
						conversationId,
						messageId,
						isDeepResearch,
						isAutoReconnect: true,
					},
				});
				const invokedFrom = streamMeta?.invokedFrom;
				trackAIMAUEvent({
					type: 'viewed',
					userGenerated: true,
					proactive: false,
					triggeredByAutoReconnect: true,
					messageId,
					singleInstrumentationID: messageId,
					deepResearchEnabled: isDeepResearch ?? false,
					citedProductList: streamMeta?.citedProductList ?? {},
					connectorInfo: streamMeta?.connectorInfo,
					sourceProducts: streamMeta?.sourceProducts ?? {},
					pluginsInvoked: streamMeta?.pluginsInvoked,
					skillsInvoked: streamMeta?.skillsInvoked,
					executedStratusSkill: streamMeta?.executedStratusSkill,
					responseSources: streamMeta?.responseSources,
					usesJsm: streamMeta?.usesJsm,
					messageActions,
					...(invokedFrom && isExperimentEnabled('aifc_invokedfrom_on_reconnect')
						? { invokedFrom }
						: {}),
					conversationChannelId: conversationId,
					timestamp: new Date().toISOString(),
					attributes: {
						...(fg('aifc_request_id_analytics') && streamMeta?.requestId
							? { request_id: streamMeta.requestId }
							: {}),
						...(fg('aifc_trace_id_analytics') && generationTraceId
							? { traceID: generationTraceId }
							: {}),
						...(streamMeta?.interactionModeExecuted
							? { interactionModeExecuted: streamMeta.interactionModeExecuted }
							: {}),
						...streamMeta?.visualBlockCounts,
						...(fg('add_mci_kr_attributes_to_rovo_chat_analytics')
							? mciKrAttributesFromSkillsAndPlugins(
									streamMeta?.skillsInvoked ?? [],
									streamMeta?.pluginsInvoked ?? [],
									streamMeta?.agentExternalConfigReference,
								)
							: {}),
					},
				});
				// Fire agentMessage received for funnel parity with processStream.
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'received',
					actionSubject: 'agentMessage',
					attributes: {
						messageId,
						humanMessageId: streamMeta?.humanMessageId,
						conversationChannelId: conversationId,
						pluginsInvoked: streamMeta?.pluginsInvoked ?? [],
						// Non-UGC authoritative-answer descriptors (gated in extractStreamMeta).
						...(streamMeta?.hasAuthoritativeSources
							? {
									hasAuthoritativeSources: true,
									authorityLevel: streamMeta.authorityLevel,
									isHighlyAuthoritative: streamMeta.isHighlyAuthoritative,
									overallConfidence: streamMeta.overallConfidence,
									authoritativeSourceCount: streamMeta.authoritativeSourceCount,
									authoritativeExpertCount: streamMeta.authoritativeExpertCount,
								}
							: {}),
						citedProductList: streamMeta?.citedProductList,
						sourceProducts: streamMeta?.sourceProducts,
						has3pApps: Object.keys(streamMeta?.sourceProducts ?? {}).some(is3pApp),
						timestamp: new Date().toISOString(),
						messageActions: streamMeta?.messageActions,
						deepResearchEnabled: isDeepResearch ?? false,
						initiator: 'rovoChat',
						deepResearchInvoked: isDeepResearch ?? false,
						scenarioId: streamMeta?.scenarioId,
						isReconnect: true,
					},
				});
			},
		};

		const updateCurrentConversation = (data: Partial<Conversation>) => {
			dispatch(
				setConversationData({
					conversationId,
					data,
				}),
			);
		};

		const cancelMessageStream = () => {
			assistanceService.cancelMessageStream({ conversationId, messageId }).catch((err: unknown) => {
				logException({
					name: 'rovoCancelMessageStream',
					exception: err,
					tags: { conversationId, messageId },
				});
			});
		};

		const messages = getMessages(conversationId);
		const answerIndex = _answerIndex ?? findStoreMessageIndex(messages, messageId);
		const originalMessage = messages[answerIndex];
		// A reconnect response identifies a new HTTP request, not the originating generation.
		const generationTraceId =
			originalMessage && isStoreAssistant(originalMessage) ? originalMessage.traceId : undefined;
		const { abortController, storeAbortController, confirmAbort } = createStoreAbortController(
			() => messageId,
		);
		let traceId: string | undefined;
		let streamMeta: StreamMeta | undefined;
		let isBackendCreationRequest = false;
		let messageActions: string[] | undefined;
		const currentLastEventId = lastEventId;
		let reconnectSucceeded = false;

		//** CONNECT TO STREAM (with retry) */
		try {
			metrics.onReconnectStarted();
			updateCurrentConversation({
				status: CHAT_STORE_STATUS.typingResponse,
				traces: [{ messageContent: 'Resuming response...', status: 'loading' }],
				storeAbortController,
			});

			const reconnect = (eventId: string | undefined) =>
				assistanceService.reconnectToMessageStream({
					conversationId,
					messageId,
					lastEventId: eventId,
					controller: abortController,
				});

			// Capture timestamp BEFORE the API call so that any live chunks
			// still in-flight during the round-trip are correctly identified
			// as live rather than replay.
			const reconnectTimestamp = Date.now();

			const { generator: initialGenerator, responseData } = await reconnect(currentLastEventId);

			traceId = responseData?.traceId;
			metrics.onReconnectEstablished();

			const wrappedGenerator = createReconnectableStream<ParsedSSEMessage<StreamMessage>>({
				initialGenerator,
				canReconnect: () => !abortController.signal.aborted,
				getReconnectGenerator: (eventId) => reconnect(eventId).then((r) => r.generator),
				getEventId: (chunk) => (chunk.id ? chunk.id : undefined),
				onReconnecting: (attempt, maxAttempts, eventId) => {
					totalAttempts = attempt;
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'retrying',
						actionSubject: 'rovoChatReconnection',
						attributes: {
							conversationId,
							messageId,
							isDeepResearch,
							attempt,
							maxAttempts,
							hasLastEventId: !!eventId,
						},
					});
					updateCurrentConversation({
						traces: [
							{
								messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
								status: 'loading',
							},
						],
					});
				},
				onReconnected: () => {
					metrics.onReconnectEstablished();
				},
				shouldContinueRetrying: () => {
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return true;
				},
				signal: abortController.signal,
			});

			// Batch store updates and chunk-merge REASONING_PART during replay;
			// both flushed together via onFlush at the replay→live boundary.
			const reasoningChunkBuffer = createReasoningChunkBuffer((mergedEvent) => {
				processStreamResearch({
					messageId,
					streamMessage: mergedEvent,
					isDeepResearch: isDeepResearch ?? false,
				});
			});

			const {
				wrapGenerator,
				batchedSetMessages,
				batchedGetMessages,
				batchedProcessStreamResearch,
				batchedUpdateCurrentConversation,
				getIsReplaying,
				getCompletedStepIds,
			} = withReplayFlush({
				conversationId,
				reconnectTimestamp,
				getMessages,
				setMessages,
				processStreamResearch,
				updateCurrentConversation,
				// Use flushFiltered so reasoning chunks for already-completed steps
				// are dropped — this prevents spurious "Rovo is thinking" rounds
				// from appearing in the research expandable after reconnect replay.
				// Steps that are still in-progress (no STEP_COMPLETED seen) are
				// flushed normally so live streaming continues correctly.
				onFlush: () =>
					reasoningChunkBuffer.flushFiltered((stepId) => !getCompletedStepIds().has(stepId)),
			});

			const isStreamTimeoutEnabled = FeatureGates.getExperimentValue(
				'rovo_chat_hung_stream_timeout',
				'isEnabled',
				false,
			);
			const streamIdleTimeoutMs = FeatureGates.getExperimentValue<number>(
				'rovo_chat_hung_stream_timeout',
				'timeoutMs',
				ROVO_CHAT_STREAM_IDLE_TIMEOUT_MS,
			);

			// Tracks whether the stream completed its success path. Used to suppress
			// the timeout if a final response arrives in the same tick as the timer fires.
			let hasReceivedFinalResponse = false;

			const rawGenerator = wrapGenerator(wrappedGenerator);
			const generatorWithTimeout = isStreamTimeoutEnabled
				? withAsyncGeneratorTimeout(rawGenerator, {
						timeoutMs: streamIdleTimeoutMs,
						// Don't time out while offline – the reconnect layer waits for the
						// client to come back online, so timing out would lose the reconnect.
						isPaused: () => typeof navigator !== 'undefined' && !navigator.onLine,
						onTimeout: () => {
							if (hasReceivedFinalResponse || abortController.signal.aborted) {
								return undefined;
							}

							abortController.abort();
							const timeoutError = new RovoChatStreamTimeoutError(NO_FINAL_RESPONSE_ERROR_MESSAGE, {
								traceId,
							});
							sendAnalyticsEvent({
								eventType: 'track',
								action: 'timedOut',
								actionSubject: 'rovoChatStream',
								attributes: {
									conversationChannelId: conversationId,
									experienceName: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
									timeoutMs: streamIdleTimeoutMs,
									timeoutType: 'idle',
									traceId,
								},
							});
							return timeoutError;
						},
					})
				: rawGenerator;

			await processReconnectStreamMessage({
				generator: generatorWithTimeout,
				traceId: fg('aifc_trace_id_analytics') ? generationTraceId : traceId,
				messageId,
				conversationId,
				answerIndex,
				isStagingArea: chatContextStoreActions.getIsStagingArea(),
				isDeepResearch,
				getState,
				setMessages: batchedSetMessages,
				getMessages: batchedGetMessages,
				processStreamResearch: batchedProcessStreamResearch,
				// skip batching for Stream Actions for now, since string accumulation is not guarnateed and vary based on the action type.
				// TODO: should be imrpoved in future snapshot event support.
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				onHumanMessageIdReplaced,
				sendAnalyticsEvent,
				updateCurrentConversation: batchedUpdateCurrentConversation,
				getIsReplaying,
				reasoningChunkBuffer,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				onStreamEnd: () => {
					// Mirror onFlush: drop reasoning for completed steps.
					reasoningChunkBuffer.flushFiltered((stepId) => !getCompletedStepIds().has(stepId));
				},
				// Fired immediately when FINAL_RESPONSE arrives — sets the outer flag so
				// the timeout wrapper won't treat a same-tick timer fire as a real timeout.
				onFinalResponse: () => {
					hasReceivedFinalResponse = true;
				},
				onTaskStatusDraft: ({ taskId, title, updatedAt, messageId: msgId }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.createTaskFromStream({
						conversationId,
						taskId,
						title,
						updatedAt,
						messageId: msgId,
					});
				},
				onTaskStatusCompleted: ({ title, updatedAt, completionMessageId }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.setTaskProgressState({
						conversationId,
						progressState: 'Completed',
						title,
						updatedAt,
						seekingCompletedAck: true,
						completionMessageId,
					});
				},
				onTodoSnapshot: ({ todos }) => {
					if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
						return;
					}
					unifiedTasksActions.setTaskTodoSnapshot({
						conversationId,
						todoSnapshot: { todos },
					});
				},
				onStreamEvent: (event, streamMessage, eventId) => {
					if (event === 'chunk') {
						metrics.onReconnectChunkReceived();

						if (streamMessage && isStreamActionComplete(streamMessage)) {
							const isBackendCreationAction =
								isServerSideExecutionAction(streamMessage.message) &&
								(isExperimentEnabled('cwr-be-based-multi-creation-v0') ||
									fg('cwr-be-based-multi-creation-v0-missing-apps'));
							isBackendCreationRequest ||= isBackendCreationAction;
							if (isBackendCreationAction) {
								messageActions = [...(messageActions ?? []), streamMessage.message.key];
							}
						}

						if (streamMessage && isStreamResponse(streamMessage)) {
							streamMeta = extractStreamMeta(streamMessage as StreamResponse);
							if (!isBackendCreationRequest) {
								messageActions = streamMeta.messageActions;
							}
						}
					}
					if (event === 'error') {
						if (isStreamAUPError(streamMessage)) {
							reconnectSucceeded = true;
							metrics.onReconnectSucceeded();
						}
					}
				},
			});

			if (abortController.signal.aborted) {
				throw new DOMException('Reconnect aborted', 'AbortError');
			}

			if (!hasReceivedFinalResponse) {
				throw new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId,
					},
				);
			}

			metrics.onReconnectSucceeded();
			onComplete?.({ success: true, streamMeta });
		} catch (error) {
			// Handle idle-stream timeout: abortController was already aborted inside onTimeout,
			// so check RovoChatStreamTimeoutError before the generic AbortError branch to distinguish
			// a timeout-triggered abort from a genuine user/system abort.
			if (error instanceof RovoChatStreamTimeoutError) {
				updateCurrentConversation({ lastAgentMessageStatus: 'FAILED' });
				// Cancel the upstream HTTP stream — the server is still open and nobody else will close it.
				cancelMessageStream();
				metrics.onReconnectFailed(error as Error);
				if (isDeepResearch) {
					markIncompleteResearchesErrored({ messageId });
				}
				onComplete?.({
					success: false,
					error: error instanceof Error ? error : new Error(String(error)),
				});
				throw error;
			}

			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				confirmAbort();
				updateCurrentConversation({ lastAgentMessageStatus: 'FAILED' });
				metrics.onReconnectAborted();
				// Only cancel the message stream if the abort reason is not the chat unmounted reason
				if (abortController.signal.reason !== RECONNECT_ABORT_REASON_CHAT_UNMOUNTED) {
					cancelMessageStream();
				}
				return;
			}

			if (!reconnectSucceeded) {
				metrics.onReconnectFailed(error as Error);
			}

			if (isDeepResearch) {
				markIncompleteResearchesErrored({ messageId });
			}

			onComplete?.({
				success: reconnectSucceeded,
				error: error instanceof Error ? error : new Error(String(error)),
			});
			throw error;
		} finally {
			endReconnection(conversationId);
			updateCurrentConversation(clearStatus({}));
			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
		}
	};
